import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
// import { Progress } from '@/components/ui/progress' // TODO: Add progress component
import {
  Star,
  ChevronDown,
  ChevronUp,
  Save,
  SkipForward,
  Mail,
  Bot,
  Clock,
  User,
  Tag,
  MessageSquare
} from 'lucide-react'

interface QATestEmail {
  uid: string
  subject: string
  body: string
  categories: string[]
  test_category: string
  customer_email: string
  word_count: number
}

interface QATestResult {
  ai_response?: string
  rating?: number
  notes?: string
  response_time_ms?: number
}

interface QASplitViewProps {
  testEmails: QATestEmail[]
  currentIndex: number
  onRating: (emailUid: string, rating: number, notes: string) => void
  onNext: () => void
  onPrevious: () => void
  preGeneratedResponses?: Record<string, string>
}

export function QASplitView({
  testEmails,
  currentIndex,
  onRating,
  onNext,
  onPrevious,
  preGeneratedResponses
}: QASplitViewProps) {
  const [selectedRating, setSelectedRating] = useState<number | null>(null)
  const [notes, setNotes] = useState('')
  const [notesExpanded, setNotesExpanded] = useState(false)
  const [aiResponse, setAiResponse] = useState<string>('')
  const [isGenerating, setIsGenerating] = useState(false)

  const currentEmail = testEmails[currentIndex]
  const progress = ((currentIndex + 1) / testEmails.length) * 100

  // Use pre-generated response if available, otherwise allow step-by-step generation
  const displayResponse = preGeneratedResponses?.[currentEmail?.uid] || aiResponse

  // Debug: Log what we have for current email
  useEffect(() => {
    console.log(`🔍 QASplitView Debug:`)
    console.log(`  Current Email UID: ${currentEmail?.uid}`)
    console.log(`  PreGenerated Keys:`, Object.keys(preGeneratedResponses || {}))
    console.log(`  Match Found:`, !!preGeneratedResponses?.[currentEmail?.uid])
    console.log(`  Display Response Length:`, displayResponse?.length || 0)
  }, [currentEmail, preGeneratedResponses, displayResponse])

  useEffect(() => {
    // Reset state when email changes
    setSelectedRating(null)
    setNotes('')
    setNotesExpanded(false)
    setAiResponse('') // Clear individual response when changing emails
  }, [currentIndex])

  const generateAIResponse = async () => {
    setIsGenerating(true)
    try {
      // Use existing EmailDrafter service for step-by-step generation
      const { emailDrafterService } = await import('@/services/email-drafter')

      const request = {
        email_address: currentEmail.customer_email || 'test@example.com',
        email_name: currentEmail.customer_email?.split('@')[0] || 'Kunde',
        email_subject: currentEmail.subject,
        email_content: currentEmail.body,
        timestamp: new Date().toISOString(),

        // Step-by-step tracking
        qa_session_id: `qa_step_by_step_${Date.now()}`,
        qa_test_uid: currentEmail.uid
      }

      const response = await emailDrafterService.sendEmailDraft(request)
      setAiResponse(response)
    } catch (error) {
      console.error('Error generating AI response:', error)
      setAiResponse(`Fehler beim Generieren der AI Antwort: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleStarClick = (rating: number) => {
    setSelectedRating(rating)
  }

  const handleSaveAndNext = () => {
    if (selectedRating !== null) {
      onRating(currentEmail.uid, selectedRating, notes)
      onNext()
    }
  }

  const getCategoryColor = (category: string) => {
    const colors = {
      'vage': 'bg-gray-500',
      'viele_fragen': 'bg-blue-500',
      'einfach': 'bg-green-500',
      'installation': 'bg-purple-500',
      'technical_issues': 'bg-red-500',
      'product_inquiry': 'bg-yellow-500',
      'pricing': 'bg-orange-500',
      'warranty': 'bg-pink-500',
      'mit_fotos': 'bg-indigo-500',
      'beschwerde': 'bg-red-600',
      'danksagung': 'bg-green-600'
    }
    return colors[category as keyof typeof colors] || 'bg-gray-400'
  }

  // AI responses are now pre-generated in batch processing

  if (!currentEmail) {
    return (
      <div className="text-center py-8">
        <p>Alle E-Mails getestet! 🎉</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Progress Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              QS Test Protokoll - E-Mail {currentIndex + 1} von {testEmails.length}
            </CardTitle>
            <Badge variant="outline">
              Session: prompt_v1.2
            </Badge>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </CardHeader>
      </Card>

      {/* Split View */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Side - Original Email */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="h-5 w-5 text-blue-600" />
              Eingehende Kunden-E-Mail
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Email Metadata */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-4 w-4" />
                <span>{currentEmail.customer_email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Tag className="h-4 w-4" />
                <Badge
                  variant="secondary"
                  className={`text-white ${getCategoryColor(currentEmail.test_category)}`}
                >
                  {currentEmail.test_category}
                </Badge>
                <span className="text-xs">
                  {currentEmail.word_count} Wörter
                </span>
              </div>
            </div>

            {/* Email Subject */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="font-medium text-blue-900 text-sm mb-1">Betreff:</div>
              <div className="text-blue-800">{currentEmail.subject}</div>
            </div>

            {/* Email Content */}
            <div className="border rounded-lg">
              <div className="bg-gray-50 px-4 py-2 border-b">
                <span className="font-medium text-gray-700">E-Mail Inhalt</span>
              </div>
              <div className="p-4 max-h-96 overflow-y-auto">
                <div className="whitespace-pre-wrap text-sm">
                  {currentEmail.body}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right Side - AI Response & Rating */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bot className="h-5 w-5 text-green-600" />
              Generierte KI Antwort
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Generate AI Response (Step-by-Step Mode) */}
            {!displayResponse && !preGeneratedResponses && (
              <div className="text-center py-8">
                <Button
                  onClick={generateAIResponse}
                  disabled={isGenerating}
                  size="lg"
                >
                  {isGenerating ? (
                    <>
                      <Clock className="mr-2 h-4 w-4 animate-spin" />
                      Generiere Antwort...
                    </>
                  ) : (
                    <>
                      <Bot className="mr-2 h-4 w-4" />
                      KI Antwort für diese E-Mail generieren
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* AI Response Status (Batch Mode) */}
            {!displayResponse && preGeneratedResponses && (
              <div className="text-center py-8 text-muted-foreground">
                <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Keine AI Antwort verfügbar für diese E-Mail.</p>
                <p className="text-sm">Starten Sie eine neue Batch-Verarbeitung.</p>
              </div>
            )}

            {/* AI Response Display */}
            {displayResponse && (
              <div className="border rounded-lg">
                <div className="bg-green-50 px-4 py-2 border-b">
                  <span className="font-medium text-green-700">KI Antwort (HTML Rendered)</span>
                </div>
                <div className="p-4 max-h-96 overflow-y-auto">
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: displayResponse }}
                  />
                </div>
              </div>
            )}

            {/* Quick Rating (1-5 Stars) */}
            {displayResponse && (
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-sm font-medium mb-2">Bewertung (1-5 Sterne)</div>
                  <div className="flex justify-center gap-1">
                    {[1, 2, 3, 4, 5].map((rating) => (
                      <button
                        key={rating}
                        onClick={() => handleStarClick(rating)}
                        className={`p-2 transition-colors ${
                          selectedRating !== null && rating <= selectedRating
                            ? 'text-yellow-400'
                            : 'text-gray-300 hover:text-yellow-300'
                        }`}
                      >
                        <Star className="h-8 w-8 fill-current" />
                      </button>
                    ))}
                  </div>
                  {selectedRating !== null && (
                    <div className="text-sm text-muted-foreground mt-1">
                      {selectedRating}/5 Sterne ausgewählt
                    </div>
                  )}
                </div>

                {/* Expandable Notes */}
                <div className="space-y-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setNotesExpanded(!notesExpanded)}
                    className="w-full justify-between"
                  >
                    <span className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Notizen hinzufügen
                    </span>
                    {notesExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>

                  {notesExpanded && (
                    <Textarea
                      placeholder="Detailliertes Feedback zur AI Antwort..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="min-h-[100px]"
                    />
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    onClick={handleSaveAndNext}
                    disabled={selectedRating === null}
                    className="flex-1"
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Speichern & Weiter
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onNext}
                    className="flex-shrink-0"
                  >
                    <SkipForward className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={currentIndex === 0}
        >
          ← Vorherige
        </Button>
        <span className="text-sm text-muted-foreground">
          {currentIndex + 1} / {testEmails.length}
        </span>
        <Button
          variant="outline"
          onClick={onNext}
          disabled={currentIndex === testEmails.length - 1}
        >
          Nächste →
        </Button>
      </div>
    </div>
  )
}